import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import contactLottie from "../assets/lottie-files/contact.lottie";
import { sanityFetch } from "../sanityClient";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" as const } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } },
};

const LightCard = ({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) => (
  <div
    style={{
      background: "#ffffff",
      border: "1px solid rgba(45,106,20,0.12)",
      borderRadius: 16,
      padding: "1.75rem",
      height: "100%",
      boxShadow: "0 2px 20px rgba(0,0,0,0.055)",
      ...style,
    }}
  >
    {children}
  </div>
);

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <div style={{ marginBottom: "2rem" }}>
    <h2
      style={{
        fontSize: "clamp(1.6rem, 3vw, 2.2rem)",
        fontWeight: 800,
        color: "#0d1b2a",
        margin: 0,
      }}
    >
      {children}
    </h2>
    <div
      style={{
        width: 56,
        height: 4,
        background: "linear-gradient(90deg, #6dc744, #4a8c25)",
        borderRadius: 2,
        marginTop: 8,
      }}
    />
  </div>
);

type StaffMember = {
  _id: string;
  initials: string;
  name: string;
  role: string;
  email: string;
  color: string;
};

const Contact: React.FC = () => {
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);

  useEffect(() => {
    sanityFetch<StaffMember[]>(
      `*[_type == "teamMember"] | order(order asc) { _id, initials, name, role, email, color }`
    ).then(setStaffMembers);
  }, []);
  return (
    <div>
      {/* HERO */}
      <section
        style={{
          background: "linear-gradient(135deg, #0a2605 0%, #0d1b2a 55%, #150308 100%)",
          padding: "5rem 1.5rem 6rem",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "absolute", top: -120, right: -120, width: 440, height: 440, borderRadius: "50%", background: "radial-gradient(circle, rgba(198,37,45,0.2) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -100, left: -100, width: 380, height: 380, borderRadius: "50%", background: "radial-gradient(circle, rgba(109,199,68,0.15) 0%, transparent 70%)", pointerEvents: "none" }} />

        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "3rem", position: "relative", zIndex: 1 }}>
          <motion.div style={{ flex: "1 1 480px", minWidth: 300 }} variants={stagger} initial="hidden" animate="visible">
            <motion.span variants={fadeUp} style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(109,199,68,0.12)", border: "1px solid rgba(109,199,68,0.3)", borderRadius: 999, padding: "0.35rem 1rem", color: "#6dc744", fontSize: "0.82rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "1rem" }}>
              <span style={{ width: 7, height: 7, background: "#6dc744", borderRadius: "50%", display: "inline-block" }} />
              Get in Touch
            </motion.span>

            <motion.h1 variants={fadeUp} style={{ fontSize: "clamp(2.4rem, 5.5vw, 4rem)", fontWeight: 900, color: "white", lineHeight: 1.08, marginBottom: "1.25rem" }}>
              We'd Love to{" "}
              <span style={{ background: "linear-gradient(90deg, #6dc744, #c6252d)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                Hear From You
              </span>
            </motion.h1>

            <motion.p variants={fadeUp} style={{ fontSize: "1.15rem", color: "rgba(255,255,255,0.72)", lineHeight: 1.7, marginBottom: "2rem", maxWidth: 520 }}>
              Have questions about the Market? Want to get involved, support local, or simply say hello? Reach out — our team is here to help.
            </motion.p>

            <motion.div variants={fadeUp} style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
              <a
                href="mailto:info@sjfm.ca"
                style={{ background: "linear-gradient(135deg, #c6252d, #a01e25)", color: "white", borderRadius: 999, padding: "0.9rem 2.2rem", fontWeight: 700, fontSize: "1rem", textDecoration: "none", boxShadow: "0 4px 20px rgba(198,37,45,0.35)", transition: "transform 0.2s, box-shadow 0.2s", display: "inline-block" }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(198,37,45,0.5)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(198,37,45,0.35)"; }}
              >
                Email Us →
              </a>
              <a
                href="tel:+17092217536"
                style={{ background: "rgba(109,199,68,0.12)", color: "#6dc744", border: "1px solid rgba(109,199,68,0.3)", borderRadius: 999, padding: "0.9rem 2.2rem", fontWeight: 700, fontSize: "1rem", textDecoration: "none", transition: "background 0.2s", display: "inline-block" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(109,199,68,0.22)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(109,199,68,0.12)"; }}
              >
                (709) 221-7536
              </a>
            </motion.div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.88 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.85, ease: "easeOut" }} style={{ flex: "1 1 360px", minWidth: 280, display: "flex", justifyContent: "center" }}>
            <DotLottieReact src={contactLottie} loop autoplay style={{ width: "100%", maxWidth: 440 }} />
          </motion.div>
        </div>
      </section>

      {/* INFO CARDS */}
      <section style={{ background: "#fffdf8", padding: "4rem 1.5rem" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "1.5rem" }}>
            {/* Hours */}
            <motion.div variants={fadeUp}>
              <LightCard style={{ borderTop: "4px solid #6dc744" }}>
                <div style={{ fontSize: "2rem", marginBottom: "0.75rem" }}>🕰️</div>
                <h3 style={{ fontSize: "1.2rem", fontWeight: 800, color: "#0d1b2a", marginBottom: "0.6rem" }}>Market Hours</h3>
                <p style={{ color: "#444", lineHeight: 1.7, margin: 0 }}>
                  <strong>Saturdays (year-round):</strong> 9:00am – 4:00pm<br />
                  <strong>Some Sundays:</strong> Check our social media for details.
                </p>
              </LightCard>
            </motion.div>

            {/* Location */}
            <motion.div variants={fadeUp}>
              <LightCard style={{ borderTop: "4px solid #c6252d" }}>
                <div style={{ fontSize: "2rem", marginBottom: "0.75rem" }}>📍</div>
                <h3 style={{ fontSize: "1.2rem", fontWeight: 800, color: "#0d1b2a", marginBottom: "0.6rem" }}>Location</h3>
                <p style={{ color: "#444", lineHeight: 1.7, marginBottom: "0.75rem" }}>
                  245 Freshwater Road<br />
                  St. John's, NL A1B 1B3<br />
                  <a href="tel:+17092217536" style={{ color: "#c6252d", fontWeight: 600, textDecoration: "none" }}>(709) 221-7536</a>
                </p>
                <div style={{ borderRadius: 12, overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
                  <iframe
                    title="St. John's Farmers' Market Map"
                    src="https://www.google.com/maps?q=245+Freshwater+Road,+St.+John's,+NL+A1B+1B3&output=embed"
                    width="100%"
                    height="180"
                    style={{ border: 0, display: "block" }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </LightCard>
            </motion.div>

            {/* Support */}
            <motion.div variants={fadeUp}>
              <LightCard style={{ borderTop: "4px solid #C19A3B" }}>
                <div style={{ fontSize: "2rem", marginBottom: "0.75rem" }}>❤️</div>
                <h3 style={{ fontSize: "1.2rem", fontWeight: 800, color: "#0d1b2a", marginBottom: "0.6rem" }}>Support the Market</h3>
                <p style={{ color: "#444", lineHeight: 1.7, marginBottom: "1rem" }}>
                  Since 2008, we've offered a place to find local foods and connect with the community. We're a non-profit supporting local farmers and fostering community bonds.
                </p>
                <a
                  href="mailto:info@sjfm.ca"
                  style={{ background: "linear-gradient(135deg, #c6252d, #a01e25)", color: "white", borderRadius: 999, padding: "0.65rem 1.6rem", fontWeight: 700, fontSize: "0.95rem", textDecoration: "none", display: "inline-block", boxShadow: "0 4px 16px rgba(198,37,45,0.28)", transition: "transform 0.2s" }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
                >
                  Get Involved →
                </a>
              </LightCard>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* MEET OUR TEAM */}
      <section style={{ background: "#f2fbee", padding: "4rem 1.5rem" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp}>
              <SectionHeading>Meet Our Team</SectionHeading>
            </motion.div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 320px), 1fr))", gap: "1.25rem", maxWidth: 980, margin: "0 auto" }}>
              {staffMembers.map((member) => (
                <motion.div key={member._id} variants={fadeUp}>
                  <LightCard style={{ display: "flex", alignItems: "center", gap: "1rem", padding: "1.25rem 1.5rem" }}>
                    <div style={{ width: 48, height: 48, borderRadius: "50%", background: `linear-gradient(135deg, ${member.color}, #0d1b2a)`, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "1rem", flexShrink: 0, boxShadow: `0 4px 12px ${member.color}44` }}>
                      {member.initials}
                    </div>
                    <div>
                      <div style={{ fontWeight: 800, color: "#0d1b2a", fontSize: "1rem" }}>{member.name}</div>
                      <div style={{ display: "inline-block", background: `${member.color}18`, color: member.color, borderRadius: 999, padding: "2px 9px", fontSize: "0.75rem", fontWeight: 700, marginBottom: "0.3rem" }}>
                        {member.role}
                      </div>
                      <div>
                        <a href={`mailto:${member.email}`} style={{ color: "#4a8c25", fontSize: "0.88rem", fontWeight: 600, textDecoration: "none" }}
                          onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
                        >
                          {member.email}
                        </a>
                      </div>
                    </div>
                  </LightCard>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* VOLUNTEER */}
      <section style={{ background: "#fffdf8", padding: "4rem 1.5rem" }}>
        <div style={{ maxWidth: 780, margin: "0 auto" }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp}>
              <SectionHeading>Volunteer With Us</SectionHeading>
            </motion.div>
            <motion.div variants={fadeUp}>
              <LightCard style={{ borderTop: "4px solid #6dc744" }}>
                <div style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}>🙌</div>
                <p style={{ color: "#444", lineHeight: 1.75, fontSize: "1.1rem", marginBottom: "0.75rem" }}>
                  Volunteering is a great way to support the St. John's Farmers' Market! By giving your time, you'll help strengthen our community and promote sustainable food systems.
                </p>
                <p style={{ color: "#444", lineHeight: 1.75, fontSize: "1.1rem", marginBottom: "1.25rem" }}>
                  Interested in volunteering? Click the button below to email us and get started!
                </p>
                <a
                  href="mailto:volunteer@sjfm.ca"
                  style={{ background: "linear-gradient(135deg, #2d6a14, #4a8c25)", color: "white", borderRadius: 999, padding: "0.9rem 2.2rem", fontWeight: 700, fontSize: "1rem", textDecoration: "none", display: "inline-block", boxShadow: "0 4px 20px rgba(45,106,20,0.3)", transition: "transform 0.2s, box-shadow 0.2s" }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(45,106,20,0.45)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(45,106,20,0.3)"; }}
                >
                  Become a Volunteer →
                </a>
              </LightCard>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
